import { CommonModule, DatePipe } from '@angular/common';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport,
} from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  QUILL_CUSTOM_OPTIONS,
  QUILL_FORMATS,
  QUILL_MODULES,
} from '@configs/index';
import { environment } from '@env/environment';
import {
  NgbAccordionModule,
  NgbCollapseModule,
  NgbDatepickerModule,
  NgbDropdownModule,
  NgbModalModule,
  NgbModule,
  NgbNavModule,
  NgbPaginationModule,
  NgbPopoverModule,
  NgbTimepickerModule,
  NgbToastModule,
  NgbTooltipModule,
  NgbTypeaheadModule,
} from '@ng-bootstrap/ng-bootstrap';
import { NgMapsCoreModule } from '@ng-maps/core';
import { GOOGLE_MAPS_API_CONFIG, NgMapsGoogleModule } from '@ng-maps/google';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { ClickPreventDefaultDirective } from '@shared/directives/click-prevent-default.directive';
import { RequireLoginDirective } from '@shared/directives/require-login.directive';
import { AttributeValuePipe } from '@shared/pipes/attribute-value.pipe';
import { Base64ImagePipe } from '@shared/pipes/base64-image.pipe';
import { YouTransformPipe } from '@shared/pipes/you-transform.pipe';
import { NgxDatatableModule } from '@siemens/ngx-datatable';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { ClickOutsideModule } from 'ng-click-outside';
import { TagInputModule } from 'ngx-chips';
import { NgxFileDropModule } from 'ngx-file-drop';
import { QuillModule } from 'ngx-quill';
import { KeyOfferModalComponent } from './components/key-offer-modal/key-offer-modal.component';
import { ActiveRouteDirective } from './directives/active-route.directive';
import { DebounceClickDirective } from './directives/debounce-click.directive';
import { DefaultAvatarDirective } from './directives/default-avatar.directive';
import { DefaultImageDirective } from './directives/default-image.directive';
import { DefaultPlaceholderDirective } from './directives/default-placeholder.directive';
import { ToggleDisplayDirective } from './directives/toggle-display.directive';
import { TooltipDirective } from './directives/tooltip.directive';
import { ParenthesesPipe } from './pipes/parentheses.pipe';

// Angular core modules
const ANGULAR_MODULES = [CommonModule, FormsModule, ReactiveFormsModule];

// Third party modules
const THIRD_PARTY_MODULES = [
  TranslateModule,
  NgSelectModule,
  NgxDatatableModule,
  NgxFileDropModule,
  ClickOutsideModule,
  TagInputModule,
  EditorModule,
  NgMapsCoreModule,
  NgMapsGoogleModule,
];

// NgBootstrap modules
const NGB_MODULES = [
  NgbModule,
  NgbAccordionModule,
  NgbCollapseModule,
  NgbDatepickerModule,
  NgbDropdownModule,
  NgbModalModule,
  NgbNavModule,
  NgbPaginationModule,
  NgbPopoverModule,
  NgbTimepickerModule,
  NgbToastModule,
  NgbTooltipModule,
  NgbTypeaheadModule,
];

// Standalone components
const STANDALONE_COMPONENTS = [
  ToggleDisplayDirective,
  KeyOfferModalComponent,
  DebounceClickDirective,
  DefaultImageDirective,
  ParenthesesPipe,
  TooltipDirective,
  ActiveRouteDirective,
  DefaultAvatarDirective,
  DefaultPlaceholderDirective,
];

// Pipes
const PIPES = [
  // Add any additional pipes here
  AttributeValuePipe,
  YouTransformPipe,
  Base64ImagePipe,
];

// Pipes
const DIRECTIVES = [
  // Add any additional pipes here
  ClickPreventDefaultDirective,
  DebounceClickDirective,
  RequireLoginDirective,
];

@NgModule({
  imports: [
    ...ANGULAR_MODULES,
    ...THIRD_PARTY_MODULES,
    ...NGB_MODULES,
    ...STANDALONE_COMPONENTS,
    ...PIPES,
    ...DIRECTIVES,
    QuillModule.forRoot({
      modules: QUILL_MODULES,
      formats: QUILL_FORMATS,
      customOptions: QUILL_CUSTOM_OPTIONS,
    }),
  ],
  declarations: [
    // ... other non-standalone declarations ...
  ],
  exports: [
    ...ANGULAR_MODULES,
    ...THIRD_PARTY_MODULES,
    ...NGB_MODULES,
    ...PIPES,
    ...DIRECTIVES,
    ...STANDALONE_COMPONENTS,
    QuillModule,
  ],
  providers: [
    DatePipe,
    {
      provide: GOOGLE_MAPS_API_CONFIG,
      useValue: {
        apiKey: environment.googleMapApiKey,
        libraries: ['places'],
      },
    },
    {
      provide: TINYMCE_SCRIPT_SRC,
      useValue:
        '/wp-content/themes/jip/assets/js/integration/tinymce/tinymce.min.js',
    },
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
  ],
})
export class SharedModule {}
